import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Plus, X, Loader2, Check, Gift, Copy, RotateCcw } from "lucide-react";

interface Voucher { id: number; code: string; value: number; remaining: number; guest_name?: string; notes?: string; expires_at?: string; status: string; created_at: string; }
function authH() { return { Authorization: `Bearer ${localStorage.getItem("ai2_token")}`, "x-hotel": localStorage.getItem("ai2_hotel") || "" }; }
function fmtDate(s?: string) { if (!s) return "—"; return new Date(s).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }); }

const STATUS_STYLE: Record<string, { color: string; bg: string }> = {
  active:    { color: "#34d399", bg: "rgba(52,211,153,0.12)" },
  used:      { color: "rgba(242,235,217,0.4)", bg: "rgba(255,255,255,0.04)" },
  expired:   { color: "#f87171", bg: "rgba(248,113,113,0.1)" },
  cancelled: { color: "#94a3b8", bg: "rgba(148,163,184,0.1)" },
};

export default function SpaVouchers({ API }: { API: string }) {
  const [vouchers, setVouchers] = useState<Voucher[]>([]);
  const [loading, setLoading]   = useState(true);
  const [genModal, setGenModal] = useState(false);
  const [redeemModal, setRedeemModal] = useState(false);
  const [genForm, setGenForm]   = useState({ value: "", guest_name: "", notes: "", expires_at: "" });
  const [redeemForm, setRedeemForm] = useState({ code: "", amount: "" });
  const [redeemResult, setRedeemResult] = useState<{ deducted: number; remaining: number } | null>(null);
  const [redeemError, setRedeemError] = useState("");
  const [saving, setSaving]     = useState(false);
  const [copied, setCopied]     = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    try { const { data } = await axios.get(`${API}/vouchers`, { headers: authH() }); setVouchers(data); }
    catch { /**/ } finally { setLoading(false); }
  }, [API]);

  useEffect(() => { fetch(); }, [fetch]);

  const generate = async () => {
    if (!genForm.value) return;
    setSaving(true);
    try {
      await axios.post(`${API}/vouchers`, genForm, { headers: authH() });
      setGenModal(false); setGenForm({ value: "", guest_name: "", notes: "", expires_at: "" }); fetch();
    } catch { /**/ } finally { setSaving(false); }
  };

  const redeem = async () => {
    setRedeemError(""); setRedeemResult(null);
    if (!redeemForm.code || !redeemForm.amount) return;
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/vouchers/redeem`, redeemForm, { headers: authH() });
      setRedeemResult({ deducted: data.deducted, remaining: data.remaining }); fetch();
    } catch (e: any) { setRedeemError(e?.response?.data?.error || "Redemption failed"); }
    finally { setSaving(false); }
  };

  const cancel = async (id: number) => {
    await axios.put(`${API}/vouchers/${id}`, { status: "cancelled" }, { headers: authH() });
    setVouchers(v => v.map(x => x.id === id ? { ...x, status: "cancelled" } : x));
  };

  const copyCode = (code: string) => { navigator.clipboard.writeText(code); setCopied(code); setTimeout(() => setCopied(null), 1500); };

  const active = vouchers.filter(v => v.status === "active");
  const others = vouchers.filter(v => v.status !== "active");

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#F2EBD9" }}>Gift Vouchers</div>
          <div style={{ fontSize: 12, color: "rgba(242,235,217,0.4)", marginTop: 2 }}>{active.length} active · ${active.reduce((s, v) => s + v.remaining, 0).toFixed(0)} outstanding</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="spa-btn-ghost" onClick={() => { setRedeemResult(null); setRedeemError(""); setRedeemForm({ code: "", amount: "" }); setRedeemModal(true); }}><RotateCcw size={14} /> Redeem</button>
          <button className="spa-btn-primary" onClick={() => setGenModal(true)}><Plus size={14} /> Generate</button>
        </div>
      </div>

      {/* Generate modal */}
      {genModal && (
        <div className="spa-modal-overlay" onClick={() => setGenModal(false)}>
          <div className="spa-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 380 }}>
            <div className="spa-modal-header"><h2 className="spa-modal-title">Generate Voucher</h2><button className="spa-modal-close" onClick={() => setGenModal(false)}><X size={16} /></button></div>
            <div className="spa-modal-body">
              <div className="spa-form-group"><label>Value ($) *</label><input type="number" min={1} value={genForm.value} onChange={e => setGenForm(f => ({ ...f, value: e.target.value }))} placeholder="e.g. 100" /></div>
              <div className="spa-form-group"><label>Guest Name (optional)</label><input value={genForm.guest_name} onChange={e => setGenForm(f => ({ ...f, guest_name: e.target.value }))} placeholder="Who is it for?" /></div>
              <div className="spa-form-row">
                <div className="spa-form-group" style={{ marginBottom: 0 }}><label>Expires</label><input type="date" value={genForm.expires_at} onChange={e => setGenForm(f => ({ ...f, expires_at: e.target.value }))} /></div>
              </div>
              <div className="spa-form-group" style={{ marginTop: 12 }}><label>Notes</label><textarea rows={2} value={genForm.notes} onChange={e => setGenForm(f => ({ ...f, notes: e.target.value }))} placeholder="Gift occasion, restrictions…" /></div>
            </div>
            <div className="spa-modal-footer">
              <button className="spa-btn-ghost" onClick={() => setGenModal(false)}>Cancel</button>
              <button className="spa-btn-primary" onClick={generate} disabled={saving || !genForm.value}>{saving ? <Loader2 size={14} className="spa-spinner" /> : <Gift size={14} />} Generate</button>
            </div>
          </div>
        </div>
      )}

      {/* Redeem modal */}
      {redeemModal && (
        <div className="spa-modal-overlay" onClick={() => setRedeemModal(false)}>
          <div className="spa-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 360 }}>
            <div className="spa-modal-header"><h2 className="spa-modal-title">Redeem Voucher</h2><button className="spa-modal-close" onClick={() => setRedeemModal(false)}><X size={16} /></button></div>
            <div className="spa-modal-body">
              <div className="spa-form-group"><label>Voucher Code</label><input value={redeemForm.code} onChange={e => setRedeemForm(f => ({ ...f, code: e.target.value.toUpperCase() }))} placeholder="SPA-XXXX-XXX" style={{ fontFamily: "monospace", letterSpacing: "0.05em" }} /></div>
              <div className="spa-form-group"><label>Amount to Deduct ($)</label><input type="number" min={0.01} step={0.01} value={redeemForm.amount} onChange={e => setRedeemForm(f => ({ ...f, amount: e.target.value }))} /></div>
              {redeemError && <div style={{ color: "#f87171", fontSize: 12, marginTop: 8 }}>{redeemError}</div>}
              {redeemResult && (
                <div style={{ marginTop: 12, padding: "12px 14px", background: "rgba(52,211,153,0.1)", border: "1px solid rgba(52,211,153,0.25)", borderRadius: 9 }}>
                  <div style={{ color: "#34d399", fontWeight: 700, fontSize: 14 }}>Redeemed ${redeemResult.deducted.toFixed(2)}</div>
                  <div style={{ color: "rgba(242,235,217,0.5)", fontSize: 12, marginTop: 3 }}>Remaining balance: ${redeemResult.remaining.toFixed(2)}</div>
                </div>
              )}
            </div>
            <div className="spa-modal-footer">
              <button className="spa-btn-ghost" onClick={() => setRedeemModal(false)}>Close</button>
              {!redeemResult && <button className="spa-btn-primary" onClick={redeem} disabled={saving || !redeemForm.code || !redeemForm.amount}>{saving ? <Loader2 size={14} className="spa-spinner" /> : <Check size={14} />} Redeem</button>}
            </div>
          </div>
        </div>
      )}

      {loading ? <div className="spa-loading"><Loader2 size={18} className="spa-spinner" /> Loading…</div> : vouchers.length === 0 ? (
        <div className="spa-fully-booked"><Gift size={28} style={{ marginBottom: 8, opacity: 0.3 }} /><br />No vouchers yet</div>
      ) : (
        <>
          {active.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 24 }}>
              {active.map(v => (
                <div key={v.id} style={{ background: "#0E1E38", border: "1px solid rgba(201,168,76,0.2)", borderRadius: 12, padding: "14px 18px", display: "flex", alignItems: "center", gap: 16 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                      <span style={{ fontFamily: "monospace", fontWeight: 800, fontSize: 14, color: "#c9a84c", letterSpacing: "0.05em" }}>{v.code}</span>
                      <button onClick={() => copyCode(v.code)} style={{ background: "none", border: "none", cursor: "pointer", color: "rgba(242,235,217,0.4)", padding: 0 }}>{copied === v.code ? <Check size={12} color="#34d399" /> : <Copy size={12} />}</button>
                    </div>
                    <div style={{ fontSize: 12, color: "rgba(242,235,217,0.45)", display: "flex", gap: 12, flexWrap: "wrap" }}>
                      {v.guest_name && <span>{v.guest_name}</span>}
                      <span>Expires: {fmtDate(v.expires_at)}</span>
                      {v.notes && <span>{v.notes}</span>}
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 18, fontWeight: 800, color: "#34d399" }}>${v.remaining.toFixed(0)}</div>
                    <div style={{ fontSize: 11, color: "rgba(242,235,217,0.3)" }}>of ${v.value.toFixed(0)}</div>
                  </div>
                  <button className="spa-action-btn danger" style={{ padding: "4px 10px", fontSize: 11 }} onClick={() => cancel(v.id)}>Cancel</button>
                </div>
              ))}
            </div>
          )}
          {others.length > 0 && (
            <>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "rgba(242,235,217,0.3)", marginBottom: 10 }}>Closed</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {others.map(v => (
                  <div key={v.id} style={{ background: "rgba(255,255,255,0.02)", borderRadius: 10, padding: "10px 14px", display: "flex", alignItems: "center", gap: 12, opacity: 0.55 }}>
                    <span style={{ fontFamily: "monospace", fontSize: 13, color: "rgba(242,235,217,0.5)" }}>{v.code}</span>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 6, ...STATUS_STYLE[v.status] }}>{v.status.toUpperCase()}</span>
                    {v.guest_name && <span style={{ fontSize: 12, color: "rgba(242,235,217,0.35)" }}>{v.guest_name}</span>}
                    <span style={{ marginLeft: "auto", fontSize: 12, color: "rgba(242,235,217,0.3)" }}>${v.value.toFixed(0)}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
